import { Theme } from "@mui/material/styles";
import { Chip, Tooltip, Card, Icon, Typography } from "@mui/material";

import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { setMessage, useMaterialUIController } from "context";

import { PagesEvento } from "../..";

import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import { ICountEvento, IEventoResponse } from "interfaces/eventos";
import { publicarEventoSite } from "services/evento";
import { siteConfig } from "types/siteConfig";

interface PropsSideNavItems {
  icon: string;
  label: string;
  href: PagesEvento;
  check?: string;
}

const sidenavItems: PropsSideNavItems[] = [
  {
    icon: "person",
    label: "Dados evento",
    href: "dados",
    check: "dadosEventos",
  },
  { icon: "settings_applications", label: "Kits", href: "kits" },
  {
    icon: "receipt_long",
    label: "Lotes",
    href: "lotes",
    check: "lotesEventos",
  },
  {
    icon: "lock",
    label: "Distancias/Categoria",
    href: "disCat",
    check: "distanciaEvento",
  },
  {
    icon: "security",
    label: "Tipo de Pagamento",
    href: "typePay",
    check: "formaPaymentoEvento",
  },
  { icon: "badge", label: "Cupom", href: "cupom" },
  { icon: "summarize_icon", label: "Lista inscritos", href: "list-inscritos" },
  { icon: "handshake", label: "Patrocinadores", href: "patrocinadores" },
  { icon: "share", label: "Compartilhar evento", href: "compartilhar-link" },
  { icon: "photo_library", label: "Fotos do evento", href: "fotos" },
  { icon: "campaign", label: "Recebimentos do Evento", href: "adiantamento" },
  { icon: "payments", label: "Despesas", href: "despesas" },
  { icon: "delete", label: "deletar evento", href: "delele-evento" },
];

const renderSidenavItems = (
  darkMode: any,
  resumoEvento: ICountEvento,
  onSelect: (page: PagesEvento) => void
) =>
  sidenavItems.map(({ icon, label, href, check }, key) => {
    const itemKey = `item-${key}`;

    return (
      <MDBox key={itemKey} component="li" pt={key === 0 ? 0 : 1}>
        <MDTypography
          component="a"
          href={`#${href}`}
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          onClick={() => onSelect(href)}
          sx={({
            borders: { borderRadius },
            functions: { pxToRem },
            palette: { light },
            transitions,
          }: Theme) => ({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: borderRadius.md,
            padding: `${pxToRem(10)} ${pxToRem(16)}`,
            transition: transitions.create("background-color", {
              easing: transitions.easing.easeInOut,
              duration: transitions.duration.shorter,
            }),

            "&:hover": {
              backgroundColor: light.main,
            },
          })}
        >
          <MDBox
            mr={1.5}
            lineHeight={1}
            color={darkMode ? "white" : "dark"}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Icon fontSize="small" sx={{ mr: 1.5 }}>
              {icon}
            </Icon>
            {label}
          </MDBox>

          <Tooltip title={`${label} obrigatório para publicar`}>
            {check &&
              check in resumoEvento &&
              resumoEvento[check as keyof ICountEvento] <= 0 && (
                <ReportProblemOutlinedIcon color="error" fontSize="small" />
              )}
          </Tooltip>
        </MDTypography>
      </MDBox>
    );
  });

interface Props {
  evento: IEventoResponse;
  onSelect: (page: PagesEvento) => void;
  onPublished?: () => void;
}

export default function SidenavEvento({
  onSelect,
  evento,
  onPublished,
}: Props): JSX.Element {
  const { url } = siteConfig;
  const urlEvento = `${url}/evento/${evento.slug}`;
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode } = controller;

  const resumoEvento = evento._count || ({} as ICountEvento);

  const validateEventForPublishing =
    resumoEvento.distanciaEvento > 0 &&
    resumoEvento.formaPaymentoEvento > 0 &&
    resumoEvento.lotesEventos;

  const colorsStatus: {
    [key: string]:
      | "default"
      | "success"
      | "error"
      | "warning"
      | "primary"
      | "secondary"
      | "info";
  } = {
    Publicado: "success",
    Cancelado: "error",
    Encerrado: "warning",
    Desativado: "error",
    Processamento: "primary",
  };

  const handlerPublicarEvento = () => {
    publicarEventoSite(evento.id).then((resp) => {
      setMessage(dispatch, {
        open: true,
        text: resp ? "Publicado com sucesso" : "Falha ao publicar",
        severity: resp ? "success" : "error",
      });
      if (resp) onPublished?.();
      onSelect("publicado-site");
    });
  };

  return (
    <Card
      sx={{
        borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
        position: "sticky",
        top: "2%",
      }}
    >
      <MDBox
        component={evento.status.tipo === "Publicado" ? "a" : "div"}
        href={evento.status.tipo === "Publicado" ? urlEvento : undefined}
        target={evento.status.tipo === "Publicado" ? "_blank" : undefined}
        rel={evento.status.tipo === "Publicado" ? "noreferrer" : undefined}
        display="flex"
        justifyContent="right"
        pt={2}
        px={2}
      >
        <Chip
          label={evento.status.tipo}
          variant="outlined"
          icon={<Icon>check</Icon>}
          color={colorsStatus[evento.status.tipo]}
          sx={{ px: 2 }}
        />
      </MDBox>

      <MDBox
        component="ul"
        display="flex"
        flexDirection="column"
        p={2}
        m={0}
        sx={{ listStyle: "none" }}
      >
        {renderSidenavItems(darkMode, resumoEvento, onSelect)}
      </MDBox>

      {evento.status.tipo !== "Publicado" ? (
        <MDBox p={2}>
          <MDButton
            disabled={!validateEventForPublishing}
            variant="gradient"
            color="info"
            fullWidth
            onClick={handlerPublicarEvento}
          >
            Publicar no site
          </MDButton>
        </MDBox>
      ) : (
        <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, p: 2 }}>
          <Icon color="info">visibility</Icon>
          <Typography variant="caption">{`Esse evento já foi visto por ${evento.quantity_access} pessoas.`}</Typography>
        </MDBox>
      )}
    </Card>
  );
}
